/**
 * Context Update Coordinator Hook
 * Prevents simultaneous context updates that cause cascade conflicts and infinite loops
 * 
 * Features:
 * - Serializes context updates to prevent race conditions
 * - Implements update queuing for delayed context changes  
 * - Provides coordination locks to prevent simultaneous changes
 * - Context synchronization flags to prevent cascade loops
 * - Performance monitoring and logging for debugging
 */

import { useState, useCallback, useRef, useEffect } from 'react';

interface ContextUpdate {
  id: string;
  contextName: string;
  updateFn: () => Promise<void> | void;
  priority: number; // Lower number = higher priority
  timestamp: number;
}

interface CoordinatorState {
  isUpdating: boolean;
  currentContext: string | null;
  updateQueue: ContextUpdate[];
  updateHistory: string[];
  lockTimeout: number | null;
}

export const useContextCoordinator = () => {
  const [state, setState] = useState<CoordinatorState>({
    isUpdating: false,
    currentContext: null,
    updateQueue: [],
    updateHistory: [],
    lockTimeout: null
  });

  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxHistoryLength = 10;
  const lockTimeoutMs = 5000; // 5 second timeout to prevent hanging locks

  // Clear any hanging locks on mount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Process the next update in the queue
   */
  const processNextUpdate = useCallback(async () => {
    setState(current => {
      if (current.updateQueue.length === 0) {
        return {
          ...current,
          isUpdating: false,
          currentContext: null,
          lockTimeout: null
        };
      }

      // Sort queue by priority, then by timestamp
      const sortedQueue = [...current.updateQueue].sort((a, b) => {
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        return a.timestamp - b.timestamp;
      });

      const nextUpdate = sortedQueue[0];
      const remainingQueue = sortedQueue.slice(1);

      // Execute the update
      (async () => {
        try {
          console.log(`🔄 [ContextCoordinator] Processing update: ${nextUpdate.contextName} (${nextUpdate.id})`);
          await nextUpdate.updateFn();
          console.log(`✅ [ContextCoordinator] Completed update: ${nextUpdate.contextName}`);
        } catch (error) {
          console.error(`❌ [ContextCoordinator] Update failed: ${nextUpdate.contextName}`, error);
        } finally {
          // Update state and process next item
          setState(prev => ({
            ...prev,
            updateQueue: remainingQueue,
            updateHistory: [
              ...prev.updateHistory.slice(-maxHistoryLength + 1),
              `${nextUpdate.contextName}:${nextUpdate.id}:${Date.now()}`
            ]
          }));

          // Process next update after a small delay to prevent stack overflow
          setTimeout(() => {
            processNextUpdate();
          }, 10);
        }
      })();

      return {
        ...current,
        updateQueue: remainingQueue,
        currentContext: nextUpdate.contextName,
        isUpdating: true,
        lockTimeout: Date.now() + lockTimeoutMs
      };
    });
  }, []);

  /**
   * Coordinate a context update to prevent conflicts
   */
  const coordinateUpdate = useCallback(async (
    contextName: string, 
    updateFn: () => Promise<void> | void,
    priority: number = 10
  ): Promise<void> => {
    const updateId = `${contextName}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return new Promise((resolve, reject) => {
      setState(current => {
        // Check for hanging locks and clear them
        if (current.lockTimeout && Date.now() > current.lockTimeout) {
          console.warn(`⚠️ [ContextCoordinator] Clearing hanging lock for: ${current.currentContext}`);
          return {
            ...current,
            isUpdating: false,
            currentContext: null,
            lockTimeout: null,
            updateQueue: []
          };
        }

        // If already updating this context, skip to prevent cascade loops
        if (current.currentContext === contextName) {
          console.log(`🔒 [ContextCoordinator] Skipping duplicate update: ${contextName}`);
          resolve();
          return current;
        }

        const wrappedUpdateFn = async () => {
          try {
            await updateFn();
            resolve();
          } catch (error) {
            reject(error);
          }
        };

        const newUpdate: ContextUpdate = {
          id: updateId,
          contextName,
          updateFn: wrappedUpdateFn,
          priority,
          timestamp: Date.now()
        };

        console.log(`📝 [ContextCoordinator] Queuing update: ${contextName} (priority: ${priority})`);

        const newQueue = [...current.updateQueue, newUpdate];

        // If not currently updating, start processing
        if (!current.isUpdating) {
          // Use setTimeout to ensure state update is processed first
          setTimeout(() => {
            processNextUpdate();
          }, 0);
        }

        return {
          ...current,
          updateQueue: newQueue
        };
      });
    });
  }, [processNextUpdate]);

  /**
   * Force clear all updates and locks (emergency function)
   */
  const clearAllUpdates = useCallback(() => {
    console.warn('🚨 [ContextCoordinator] Force clearing all updates and locks');
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    setState({
      isUpdating: false,
      currentContext: null,
      updateQueue: [],
      updateHistory: [],
      lockTimeout: null
    });
  }, []);

  /**
   * Check if a specific context is currently updating
   */
  const isContextUpdating = useCallback((contextName: string): boolean => {
    return state.currentContext === contextName;
  }, [state.currentContext]);

  /**
   * Get current coordinator status for debugging
   */
  const getCoordinatorStatus = useCallback(() => {
    return {
      isUpdating: state.isUpdating,
      currentContext: state.currentContext,
      queueLength: state.updateQueue.length,
      queuedContexts: state.updateQueue.map(u => u.contextName),
      updateHistory: state.updateHistory,
      hasHangingLock: state.lockTimeout ? Date.now() > state.lockTimeout : false
    };
  }, [state]);

  return {
    coordinateUpdate,
    isUpdating: state.isUpdating,
    currentContext: state.currentContext,
    queueLength: state.updateQueue.length,
    isContextUpdating,
    getCoordinatorStatus,
    clearAllUpdates
  };
};

export default useContextCoordinator;
